import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTestId } from '../../hooks/useTestId';
import { breakpoint } from '../../Themes/screensize';
import { LargeFormControl } from '../Input/Input';

export const VerificationCodeContainer = styled.div`
  display: flex;
  flex-flow: row;
  gap: 1rem;
`;

// Picking up className for styled components
export interface IVerificationCodeProps extends Pick<React.HtmlHTMLAttributes<HTMLDivElement>, 'className'>, Pick<HTMLInputElement, 'type'> {
  length?: number;
  validCodeCharacterRegex?: RegExp;
  onChange: (code: string) => void;
  disabled?: boolean;
  resetAll?: boolean;
}

const EMPTY_STRING = '';
const BACKSPACE = 'Backspace';
const DELETE = 'Delete';

export const VerificationCodeControl = styled(LargeFormControl)`
  height: 4.5rem;
  max-width: 4rem;
  font-size: 2rem;
  :focus {
    background: ${({ theme }) => theme.colours.select.input.focus.background};
    border-color: ${({ theme }) => theme.colours.select.input.focus.background};
  }
  @media (max-width: ${breakpoint.mobile}) {
    max-width: 2.875rem;
    height: 3.125rem;
  }
`;

export const VerificationCode: React.FC<IVerificationCodeProps> = ({
  length = 6,
  validCodeCharacterRegex = /^\d*$/,
  onChange,
  disabled,
  resetAll,
  type,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'VerificationCode');
  const inputsRef = useRef<Array<HTMLInputElement>>([]);

  const sendResult = () => {
    const code = inputsRef.current.map((input) => input.value).join(EMPTY_STRING);
    onChange(code);
  };

  useEffect(() => {
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }

    if (resetAll) {
      inputsRef.current.forEach((input) => (input.value = EMPTY_STRING));
      sendResult();
    }
  }, [resetAll]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (validCodeCharacterRegex.test(e.target.value)) {
      if (index < inputsRef.current.length - 1) {
        inputsRef.current[index + 1].focus();
        e.preventDefault();
      }
    } else {
      e.target.value = EMPTY_STRING;
    }
    sendResult();
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    const { key } = e;
    if (key !== BACKSPACE && key !== DELETE) return;

    const target = e.target as HTMLInputElement;
    if (target.value === EMPTY_STRING) {
      if (index > 0) {
        inputsRef.current[index - 1].focus();
        e.preventDefault();
      }
    } else {
      target.value = EMPTY_STRING;
    }
    sendResult();
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const value = e.clipboardData.getData('Text');
    if (!validCodeCharacterRegex.test(value)) {
      e.preventDefault();
      return;
    }
    const activeElementIndex = inputsRef.current.indexOf(document.activeElement as any);
    let indexOfToBeCopiedCharacter = 0;
    for (let i = activeElementIndex; i < length; i++) {
      if (indexOfToBeCopiedCharacter === value.length) {
        (inputsRef.current[activeElementIndex + indexOfToBeCopiedCharacter] as HTMLInputElement).focus();
        sendResult();
        e.preventDefault();
        return;
      }
      inputsRef.current[i].value = value.charAt(indexOfToBeCopiedCharacter);
      indexOfToBeCopiedCharacter += 1;
    }

    (inputsRef.current[activeElementIndex + indexOfToBeCopiedCharacter - 1] as HTMLInputElement).focus();
    sendResult();
    e.preventDefault();
  };

  return (
    <VerificationCodeContainer {...rest} {...generateTestId()}>
      {[...Array.from({ length })].map((value, index) => (
        <VerificationCodeControl
          key={index}
          onChange={(e) => handleOnChange(e, index)}
          onKeyDown={(e) => handleOnKeyDown(e, index)}
          onFocus={(e) => handleOnFocus(e)}
          onPaste={handleOnPaste}
          type={type}
          inputMode={type === 'number' ? 'numeric' : 'text'}
          ref={(el: HTMLInputElement) => (inputsRef.current[index] = el)}
          maxLength={1}
          disabled={disabled}
          {...generateTestId(`Control${index}`)}
        />
      ))}
    </VerificationCodeContainer>
  );
};
